import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import {
  GeodataMarker,
  GeodataMarkerEstimate,
} from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { GcpModel } from '../../../../../../typings/api/skymap/rest/v1/.common';
import { isDefined } from '../../../../utils/variables';
import { useDialog } from '../../../hooks/use-dialog';
import { Button } from '../../button/button';
import { CheckBox } from '../../check-box/check-box';
import { Dialog } from '../../dialog/dialog';
import { DropdownSuggestion } from '../../dropdown-suggestion/dropdown-suggesion';
import { Icon } from '../../icon/icon';
import { IconPanel } from '../../icon-panel/icon-panel';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { Slider } from '../../slider/slider';
import { TextBox } from '../../text-box/text-box';
import { TooltipInfoIcon } from '../../tooltip-info-icon/tooltip-info-icon';
import { GeodataImageItem, GeodataImagesContext } from '../geodata-images-state';
import { GeodataContext } from '../geodata-state';
import { AlignerContext } from './aligner-state';
import { GeodataSpatialReferenceContext } from './geodata-spatial-reference-state';
import { MarkerEstimateCard } from './marker-estimate-card';
import { pinnedGreen } from './marker-svg-utils';
import { PlaceMarkersContext } from './place-markers-state';
import { computeObservationVector, selectDiverseEstimates } from './select-diverse-estimates';
import { usePatchMarkerEstimatesMutation } from './use-patch-marker-estimates-mutation';
import { usePutMarkerMutation } from './use-put-marker-mutation';
import { useUnpinMarkersCallback } from './use-unpin-markers-callback';

const markerStatuses = ['PENDING', 'ACCEPTED', 'REJECTED', 'PINNED', 'UNPINNED'] as const;

export function computeAutoSelectionLimitForCount(total: number): number {
  return Math.min(total, Math.max(5, Math.floor(total / 2)));
}

/**
 * The default minimum confidence: at most 95%, and low enough on the slider's 1% steps that at
 * least 90% of the estimates reach it, so that a project with low confidence overall still shows
 * its estimates.
 */
export function computeDefaultMinConfidence(confidences: number[]): number {
  const sorted = [...confidences].sort((a, b) => a - b);
  const tenthPercentile = sorted[Math.floor(sorted.length / 10)] ?? 1;
  return Math.min(0.95, Math.floor(tenthPercentile * 100) / 100);
}

type MarkerStatus = (typeof markerStatuses)[number];

function getDefaultStatusFilter(
  estimates: GeodataMarkerEstimate[],
  markers: GeodataMarker[],
): MarkerStatus {
  const hasPendingOrAccepted = estimates.some(
    (e) => e.status === 'PENDING' || e.status === 'ACCEPTED',
  );
  if (hasPendingOrAccepted) {
    return 'PENDING';
  }

  const hasPinned = markers.some((m) => m.pinned);
  if (hasPinned) {
    return 'PINNED';
  }

  return 'UNPINNED';
}

type MarkerEstimateContect = {
  marker: GeodataMarker;
  estimate?: Required<GeodataMarkerEstimate>; // Optional for PINNED/UNPINNED
  gcp: GcpModel;
  image: GeodataImageItem;
  status: MarkerStatus;
};

export const ReviewGcpMarkerEstimatesDialog = (props: { dialog: ReturnType<typeof useDialog> }) => {
  const context = useContext(GeodataContext);
  const { readyToOptimize } = useContext(AlignerContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const { geodata } = context;
  const {
    estimates,
    markers,
    selectGcp,
    selectImage,
    setShowPlaceMarkersDialog,
    showPlaceMarkersDialog,
    imagesWithMarkers,
    setSortedImageList,
    isLoadingEstimates,
  } = useContext(PlaceMarkersContext);
  const { images } = useContext(GeodataImagesContext);
  const { getImageLocalPosition, origo } = useContext(GeodataSpatialReferenceContext);
  const { patchMarkerEstimates, patchMarkerEstimatesAsync, isPending } =
    usePatchMarkerEstimatesMutation(geodata.id);
  const { putMarker: putMarkerMutation } = usePutMarkerMutation();
  const { updateMarker } = useContext(PlaceMarkersContext);
  const { unpinMarkers: unpinMarkersCallback, isPending: isUnpinMarkersPending } =
    useUnpinMarkersCallback();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<string>('20');
  const [zoomLevel, setZoomLevel] = useState(3); // Default 3%
  // Follows the estimates as they arrive until the user moves the slider
  const [chosenMinConfidence, setMinConfidence] = useState<number>();
  const defaultMinConfidence = useMemo(
    () =>
      computeDefaultMinConfidence(
        estimates.filter((e) => isDefined(e.position)).map((e) => e.confidence ?? 0),
      ),
    [estimates],
  );
  const minConfidence = chosenMinConfidence ?? defaultMinConfidence;
  const [estimatesPerGcpLimit, setEstimatesPerGcpLimit] = useState<string>('5');
  const [autoSelection, setAutoSelection] = useState(true);
  const [selectedGcpFilter, setSelectedGcpFilter] = useState<string>('all');
  // Follows the estimates and markers as they load until the user picks a status
  const [chosenStatusFilter, setSelectedStatusFilter] = useState<MarkerStatus>();
  const defaultStatusFilter = useMemo(
    () => getDefaultStatusFilter(estimates, markers),
    [estimates, markers],
  );
  const selectedStatusFilter = chosenStatusFilter ?? defaultStatusFilter;
  const hasAiEstimates = useMemo(() => {
    return estimates.length > 0;
  }, [estimates]);

  const disableEstimatesPerGcpLimit = useMemo(() => {
    return (['ACCEPTED', 'REJECTED', 'PINNED'] as MarkerStatus[]).includes(selectedStatusFilter);
  }, [selectedStatusFilter]);

  const estimatesByMarkerId = useMemo(() => {
    const estimatesMap = new Map<string, MarkerEstimateContect[]>();
    const markersById = new Map(markers.map((m) => [m.id, m]));
    const gcpsById = new Map(geodata.gcpCollection?.points.map((p) => [p.id, p]));
    const imagesById = new Map(images.map((i) => [i.imageId, i]));
    // GCP and image pairs that already have an ACCEPTED estimate
    const acceptedGcpImageKeys = new Set<string>();
    for (const estimate of estimates) {
      const marker = markersById.get(estimate.markerId);
      if (estimate.status === 'ACCEPTED' && marker) {
        acceptedGcpImageKeys.add(`${marker.gcpId}/${marker.imageId}`);
      }
    }

    // Process estimates
    for (const estimate of estimates) {
      if (!isDefined(estimate.position)) {
        continue;
      }

      const marker = markersById.get(estimate.markerId);
      const gcp = marker && gcpsById.get(marker.gcpId);
      const image = marker && imagesById.get(marker.imageId);
      if (!marker || !gcp || !image) {
        continue;
      }

      // Skip pinned markers here - they'll be added separately
      if (marker.pinned) {
        continue;
      }

      if (!estimatesMap.has(estimate.markerId)) {
        estimatesMap.set(estimate.markerId, []);
      }

      estimatesMap.get(estimate.markerId)!.push({
        marker,
        estimate: { ...estimate, position: estimate.position },
        gcp,
        image,
        status: estimate.status,
      });
    }

    // Add markers (PINNED/UNPINNED)
    for (const marker of markers) {
      const gcp = gcpsById.get(marker.gcpId);
      const image = imagesById.get(marker.imageId);
      if (!gcp || !image) {
        continue;
      }

      // For unpinned markers, filter away any imageIds that already have an ACCEPTED estimate
      if (!marker.pinned && acceptedGcpImageKeys.has(`${marker.gcpId}/${marker.imageId}`)) {
        continue;
      }

      const markerId = marker.id;
      if (!estimatesMap.has(markerId)) {
        estimatesMap.set(markerId, []);
      }

      estimatesMap.get(markerId)!.push({
        marker,
        gcp,
        image,
        status: marker.pinned ? 'PINNED' : 'UNPINNED',
      });
    }

    return estimatesMap;
  }, [estimates, markers, geodata.gcpCollection, images]);

  const estimatesByGcpIdAndStatus = useMemo(() => {
    // First, group estimates by GCP ID and status
    const estimatesByGcpIdAndStatus = new Map<
      string,
      Map<string, (MarkerEstimateContect & { markerId: string })[]>
    >();

    for (const [markerId, markerEstimates] of estimatesByMarkerId.entries()) {
      for (const estimateContext of markerEstimates) {
        const gcpId = estimateContext.gcp.id || '';
        const status = estimateContext.status;

        if (!estimatesByGcpIdAndStatus.has(gcpId)) {
          estimatesByGcpIdAndStatus.set(gcpId, new Map());
        }
        const statusMap = estimatesByGcpIdAndStatus.get(gcpId)!;

        if (!statusMap.has(status)) {
          statusMap.set(status, []);
        }
        statusMap.get(status)!.push({
          markerId,
          ...estimateContext,
        });
      }
    }

    return estimatesByGcpIdAndStatus;
  }, [estimatesByMarkerId]);

  const acceptedCountByGcpId = useMemo(() => {
    const acceptedCountByGcpId = new Map<string, number>();
    for (const [gcpId, statusMap] of estimatesByGcpIdAndStatus.entries()) {
      acceptedCountByGcpId.set(
        gcpId,
        (statusMap.get('ACCEPTED')?.length || 0) + (statusMap.get('PINNED')?.length || 0),
      );
    }
    return acceptedCountByGcpId;
  }, [estimatesByGcpIdAndStatus]);

  const acceptedCount = useMemo(() => {
    return acceptedCountByGcpId.get(selectedGcpFilter) || 0;
  }, [acceptedCountByGcpId, selectedGcpFilter]);

  const selectedGcpEffectiveLimit = useMemo(() => {
    if (disableEstimatesPerGcpLimit) {
      return undefined;
    }
    if (selectedGcpFilter === '' || selectedGcpFilter === 'all') {
      return undefined;
    }
    const statusMap = estimatesByGcpIdAndStatus.get(selectedGcpFilter);
    if (!statusMap) {
      return undefined;
    }
    if (autoSelection) {
      const remaining = statusMap.get(selectedStatusFilter)?.length ?? 0;
      const accepted =
        (statusMap.get('ACCEPTED')?.length ?? 0) + (statusMap.get('PINNED')?.length ?? 0);
      return computeAutoSelectionLimitForCount(remaining + accepted);
    }
    const parsed = parseInt(estimatesPerGcpLimit, 10);
    return isNaN(parsed) || parsed <= 0 ? undefined : parsed;
  }, [
    autoSelection,
    disableEstimatesPerGcpLimit,
    selectedGcpFilter,
    selectedStatusFilter,
    estimatesByGcpIdAndStatus,
    estimatesPerGcpLimit,
  ]);

  // Check if limit is active and accepted count meets or exceeds the limit
  // Only show "Done" message when filtering by PENDING or UNPINNED and a specific GCP is selected
  const showDoneMessage = useMemo(() => {
    if (selectedStatusFilter !== 'PENDING' && selectedStatusFilter !== 'UNPINNED') {
      return false;
    }
    if (selectedGcpFilter === '' || selectedGcpFilter === 'all') {
      return false;
    }
    if (!isDefined(selectedGcpEffectiveLimit) || selectedGcpEffectiveLimit <= 0) {
      return false;
    }
    return acceptedCount >= selectedGcpEffectiveLimit;
  }, [selectedStatusFilter, selectedGcpFilter, selectedGcpEffectiveLimit, acceptedCount]);

  // Flatten the map to an array of the marker items of the selected GCP and status, limited per GCP
  const filteredMarkerItems = useMemo(() => {
    // Process each GCP: sort by confidence first, then by distance from center; apply limit
    const items: (MarkerEstimateContect & { markerId: string })[] = [];
    const status = selectedStatusFilter;

    const getCamVec3 = (e: MarkerEstimateContect): [number, number, number] => {
      if (!e.image.transform || !getImageLocalPosition) {
        return [0, 0, 1];
      }
      const p = getImageLocalPosition(e.image.transform);
      return [p.x, p.y, p.z];
    };

    const getGcpVec3 = (e: MarkerEstimateContect): [number, number, number] =>
      origo ? [e.gcp.enu[0] - origo.x, e.gcp.enu[1] - origo.y, e.gcp.enu[2] - origo.z] : e.gcp.enu;

    for (const [gcpId, statusMap] of estimatesByGcpIdAndStatus.entries()) {
      if (selectedGcpFilter !== '' && selectedGcpFilter !== 'all' && gcpId !== selectedGcpFilter) {
        continue;
      }
      const statusEstimates = statusMap.get(status);
      if (!statusEstimates) {
        continue;
      }

      // Count ACCEPTED items for this GCP
      const acceptedCount = acceptedCountByGcpId.get(gcpId) || 0;

      // Filter out estimates below min confidence (always include items without estimate, e.g. PINNED/UNPINNED)
      const aboveThreshold = statusEstimates.filter(
        (e) => e.estimate == null || (e.estimate.confidence ?? 0) >= minConfidence,
      );
      // For PENDING and UNPINNED, compute the limit from this status's own count so that
      // e.g. a GCP with 15 PENDING and 5 UNPINNED-only markers uses 15 for PENDING and 20 for UNPINNED.
      let effectiveLimit: number | undefined;
      if (!disableEstimatesPerGcpLimit && (status === 'PENDING' || status === 'UNPINNED')) {
        const gcpStatusLimit = autoSelection
          ? computeAutoSelectionLimitForCount(statusEstimates.length + acceptedCount)
          : (() => {
              if (estimatesPerGcpLimit.trim() === '') {
                return undefined;
              }
              const parsed = parseInt(estimatesPerGcpLimit, 10);
              return isNaN(parsed) || parsed <= 0 ? undefined : parsed;
            })();
        if (isDefined(gcpStatusLimit)) {
          effectiveLimit = Math.max(0, gcpStatusLimit - acceptedCount);
        }
      }

      const resolvedLimit =
        isDefined(effectiveLimit) && !isNaN(effectiveLimit)
          ? effectiveLimit
          : aboveThreshold.length;

      // Pre-seeded vectors from already-committed markers (ACCEPTED + PINNED) for this GCP,
      // so the diversity selection avoids angles already covered by existing picks.
      const preSeeded =
        status === 'PENDING' || status === 'UNPINNED'
          ? [...(statusMap.get('ACCEPTED') ?? []), ...(statusMap.get('PINNED') ?? [])].map((e) =>
              computeObservationVector(getCamVec3(e), getGcpVec3(e)),
            )
          : [];

      const estimatesToShow = selectDiverseEstimates(
        aboveThreshold,
        getCamVec3,
        getGcpVec3,
        (e) => e.estimate?.confidence ?? 1,
        resolvedLimit,
        0.5,
        preSeeded,
      );

      items.push(...estimatesToShow);
    }

    // Sort by GCP name descending
    return items.sort((a, b) => b.gcp.name.localeCompare(a.gcp.name));
  }, [
    estimatesByGcpIdAndStatus,
    selectedGcpFilter,
    selectedStatusFilter,
    autoSelection,
    estimatesPerGcpLimit,
    disableEstimatesPerGcpLimit,
    acceptedCountByGcpId,
    minConfidence,
    getImageLocalPosition,
    origo,
  ]);

  const statusIcons = useMemo(() => {
    return {
      PENDING: <Icon color={theme.color.brand.dark} fixedWidth={true} icon={['fas', 'clock']} />,
      ACCEPTED: <Icon color={theme.color.green} fixedWidth={true} icon={['fas', 'circle-check']} />,
      REJECTED: <Icon color={theme.color.red} fixedWidth={true} icon={['fas', 'circle-xmark']} />,
      PINNED: <Icon color={pinnedGreen} fixedWidth={true} icon={['fas', 'flag']} />,
      UNPINNED: <Icon color={theme.color.blue} fixedWidth={true} icon={['fas', 'flag']} />,
    };
  }, [theme]);

  // Get unique GCPs with status counts for the filter dropdown (using total counts, not filtered)
  const uniqueGcpsWithCounts = useMemo(() => {
    const gcpMap = new Map<string, { gcp: GcpModel; counts: Map<string, number> }>();

    // Count statuses per GCP from all estimates (not filtered)
    for (const [_, markerEstimates] of estimatesByMarkerId.entries()) {
      for (const estimateContext of markerEstimates) {
        const gcpId = estimateContext.gcp.id || '';
        if (!gcpMap.has(gcpId)) {
          gcpMap.set(gcpId, { gcp: estimateContext.gcp, counts: new Map<string, number>() });
        }
        const gcpData = gcpMap.get(gcpId)!;
        const currentCount = gcpData.counts.get(estimateContext.status) || 0;
        gcpData.counts.set(estimateContext.status, currentCount + 1);
      }
    }

    // Format GCP names with counts
    return Array.from(gcpMap.values())
      .map(({ gcp, counts }) => {
        const statusCounts: React.ReactNode[] = [];

        for (const status of markerStatuses) {
          const count = counts.get(status) || 0;
          if (count > 0) {
            const statusLabel = statusIcons[status];
            statusCounts.push(
              <React.Fragment key={status}>
                {count}
                {statusLabel}
              </React.Fragment>,
            );
          }
        }

        const countText =
          statusCounts.length > 0 ? (
            <span>
              {' ('}
              {statusCounts.map((item, index) => (
                <React.Fragment key={index}>
                  {index > 0 && '  '}
                  {item}
                </React.Fragment>
              ))}
              {')'}
            </span>
          ) : null;

        const displayName = (
          <span>
            {gcp.name}
            {countText}
          </span>
        );

        // Build filterName string for searching
        const filterNameParts: string[] = [];
        for (const status of markerStatuses) {
          const count = counts.get(status) || 0;
          if (count > 0) {
            filterNameParts.push(`${count}${status}`);
          }
        }
        const filterName = `${gcp.name}${filterNameParts.length > 0 ? ` (${filterNameParts.join('  ')})` : ''}`;

        return {
          id: gcp.id || '',
          name: {
            filterName,
            node: displayName,
          },
          gcp,
        };
      })
      .sort((a, b) => b.gcp.name.localeCompare(a.gcp.name));
  }, [estimatesByMarkerId, statusIcons]);

  // Parse items per page value
  const parsedItemsPerPage = itemsPerPage.trim() === '' ? 20 : parseInt(itemsPerPage, 10);
  const validItemsPerPage =
    isNaN(parsedItemsPerPage) || parsedItemsPerPage <= 0 ? 20 : parsedItemsPerPage;

  // Reset to page 1 when marker items change or items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredMarkerItems.length, validItemsPerPage]);

  // Pagination logic
  const totalPages = Math.ceil(filteredMarkerItems.length / validItemsPerPage);
  const startIndex = (currentPage - 1) * validItemsPerPage;
  const endIndex = startIndex + validItemsPerPage;
  const currentPageItems = filteredMarkerItems.slice(startIndex, endIndex);
  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;

  const handleNextPage = useCallback(() => {
    if (hasNextPage) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [hasNextPage]);

  const handlePreviousPage = useCallback(() => {
    if (hasPreviousPage) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [hasPreviousPage]);

  // Calculate how many estimates will be accepted from the current page only
  const acceptAllCount = useMemo(() => {
    let count = 0;

    // Group current page items by markerId to find the highest confidence estimate per marker
    const pageItemsByMarkerId = new Map<string, (MarkerEstimateContect & { markerId: string })[]>();
    for (const item of currentPageItems) {
      if (!pageItemsByMarkerId.has(item.markerId)) {
        pageItemsByMarkerId.set(item.markerId, []);
      }
      pageItemsByMarkerId.get(item.markerId)!.push(item);
    }

    for (const [_, markerItems] of pageItemsByMarkerId.entries()) {
      // Filter out REJECTED estimates and items without estimates (PINNED/UNPINNED)
      const pendingEstimates = markerItems.filter(
        (e) => e.estimate && e.estimate.status === 'PENDING',
      );

      // Skip if there are no PENDING estimates or if there is already an ACCEPTED estimate.
      if (
        pendingEstimates.length === 0 ||
        markerItems.some((e) => e.estimate && e.estimate.status === 'ACCEPTED')
      ) {
        continue;
      }

      count++;
    }
    return count;
  }, [currentPageItems]);

  // Check if there are any estimates that can be accepted
  const canAcceptAny = acceptAllCount > 0;

  // Calculate how many items will be rejected from filtered items
  const rejectAllCount = useMemo(() => {
    if (selectedStatusFilter !== 'PINNED' && selectedStatusFilter !== 'ACCEPTED') {
      return 0;
    }
    return filteredMarkerItems.filter((item) => item.status === selectedStatusFilter).length;
  }, [filteredMarkerItems, selectedStatusFilter]);

  // Check if there are any items that can be rejected
  const canRejectAny = rejectAllCount > 0;

  const handleAcceptAllEstimates = useCallback(() => {
    // Accept estimates from the current page only
    const estimateIdsToAccept: string[] = [];

    // Group current page items by markerId to find the highest confidence estimate per marker
    const pageItemsByMarkerId = new Map<string, (MarkerEstimateContect & { markerId: string })[]>();
    for (const item of currentPageItems) {
      if (!pageItemsByMarkerId.has(item.markerId)) {
        pageItemsByMarkerId.set(item.markerId, []);
      }
      pageItemsByMarkerId.get(item.markerId)!.push(item);
    }

    for (const [_, markerItems] of pageItemsByMarkerId.entries()) {
      // Filter out REJECTED estimates and items without estimates (PINNED/UNPINNED)
      const pendingEstimates = markerItems.filter(
        (e) => e.estimate && e.estimate.status === 'PENDING',
      );

      // Skip if there are no PENDING estimates or if there is already an ACCEPTED estimate.
      if (
        pendingEstimates.length === 0 ||
        markerItems.some((e) => e.estimate && e.estimate.status === 'ACCEPTED')
      ) {
        continue;
      }

      // Find the estimate with the highest confidence
      const highestConfidenceEstimate = pendingEstimates.reduce((prev, current) =>
        (current.estimate?.confidence || 0) > (prev.estimate?.confidence || 0) ? current : prev,
      );

      if (highestConfidenceEstimate.estimate?.id) {
        estimateIdsToAccept.push(highestConfidenceEstimate.estimate.id);
      }
    }

    if (estimateIdsToAccept.length > 0) {
      patchMarkerEstimates({
        geodataId: geodata.id,
        body: {
          ids: estimateIdsToAccept,
          status: 'ACCEPTED',
        },
      });
    }
  }, [currentPageItems, patchMarkerEstimates, geodata.id]);

  const handleAcceptEstimate = useCallback(
    async (estimateId: string) => {
      await patchMarkerEstimatesAsync({
        geodataId: geodata.id,
        body: {
          ids: [estimateId],
          status: 'ACCEPTED',
        },
      });
    },
    [patchMarkerEstimatesAsync, geodata.id],
  );

  const handleRejectEstimate = useCallback(
    async (estimateId: string) => {
      await patchMarkerEstimatesAsync({
        geodataId: geodata.id,
        body: {
          ids: [estimateId],
          status: 'REJECTED',
        },
      });
    },
    [patchMarkerEstimatesAsync, geodata.id],
  );

  const handlePinMarker = useCallback(
    async (markerId: string, gcpId: string, imageId: string, position: [number, number]) => {
      const params = {
        path: {
          geodataId: geodata.id,
          markerId: markerId,
        },
        body: {
          gcpId: gcpId,
          imageId: imageId,
          pinned: true,
          position: position,
        },
      };
      const result = await putMarkerMutation(params);
      updateMarker(result.data);
    },
    [putMarkerMutation, geodata.id, updateMarker],
  );

  const handleUnpinMarker = useCallback(
    async (markerId: string, gcpId: string, imageId: string, position: [number, number]) => {
      const params = {
        path: {
          geodataId: geodata.id,
          markerId: markerId,
        },
        body: {
          gcpId: gcpId,
          imageId: imageId,
          pinned: false,
          position: position,
        },
      };
      const result = await putMarkerMutation(params);
      updateMarker(result.data);
    },
    [putMarkerMutation, geodata.id, updateMarker],
  );

  const handleRejectAll = useCallback(async () => {
    if (selectedStatusFilter === 'PINNED') {
      const pinnedMarkers = filteredMarkerItems
        .filter((item) => item.status === 'PINNED')
        .map((item) => item.marker);

      if (pinnedMarkers.length > 0) {
        await unpinMarkersCallback({
          geodataId: geodata.id,
          markers: pinnedMarkers,
          updateMarker,
        });
      }
    } else if (selectedStatusFilter === 'ACCEPTED') {
      const estimateIds = filteredMarkerItems
        .filter((item) => item.status === 'ACCEPTED' && item.estimate?.id)
        .map((item) => item.estimate!.id);

      if (estimateIds.length > 0) {
        patchMarkerEstimates({
          geodataId: geodata.id,
          body: {
            ids: estimateIds,
            status: 'REJECTED',
          },
        });
      }
    }
  }, [
    filteredMarkerItems,
    selectedStatusFilter,
    unpinMarkersCallback,
    geodata.id,
    updateMarker,
    patchMarkerEstimates,
  ]);

  const handleImageClick = useCallback(
    (item: MarkerEstimateContect & { markerId: string }) => {
      // Find the image from imagesWithMarkers that matches the item's image
      const imageWithMarkers = imagesWithMarkers.find((img) => img.imageId === item.image.imageId);

      if (!imageWithMarkers || !item.gcp.id) {
        return;
      }

      setSortedImageList(
        filteredMarkerItems.map((i) => ({
          imageId: i.image.imageId!,
          gcpId: i.gcp.id ?? undefined,
        })),
      );

      selectGcp(item.gcp);
      selectImage(imageWithMarkers);
      setShowPlaceMarkersDialog(true);
    },
    [
      filteredMarkerItems,
      imagesWithMarkers,
      selectGcp,
      selectImage,
      setShowPlaceMarkersDialog,
      setSortedImageList,
    ],
  );

  const statusOptions = useMemo<
    { id: MarkerStatus; name: string | { filterName: string; node: React.ReactNode } }[]
  >(() => {
    const pendingLabel = t('reviewGcpMarkerEstimatesDialog.status.pending', {
      ns: 'cloudProcessing',
    });
    const acceptedLabel = t('reviewGcpMarkerEstimatesDialog.status.accepted', {
      ns: 'cloudProcessing',
    });
    const rejectedLabel = t('reviewGcpMarkerEstimatesDialog.status.rejected', {
      ns: 'cloudProcessing',
    });
    const pinnedLabel = t('reviewGcpMarkerEstimatesDialog.status.pinned', {
      ns: 'cloudProcessing',
    });
    const unpinnedLabel = t('reviewGcpMarkerEstimatesDialog.status.unpinned', {
      ns: 'cloudProcessing',
    });

    const options: {
      id: MarkerStatus;
      name: string | { filterName: string; node: React.ReactNode };
    }[] = [];

    if (hasAiEstimates) {
      options.push({
        id: 'PENDING',
        name: {
          filterName: pendingLabel,
          node: (
            <DropdownItemSpan>
              {statusIcons.PENDING}
              {pendingLabel}
            </DropdownItemSpan>
          ),
        },
      });
      options.push({
        id: 'ACCEPTED',
        name: {
          filterName: acceptedLabel,
          node: (
            <DropdownItemSpan>
              {statusIcons.ACCEPTED}
              {acceptedLabel}
            </DropdownItemSpan>
          ),
        },
      });
      options.push({
        id: 'REJECTED',
        name: {
          filterName: rejectedLabel,
          node: (
            <DropdownItemSpan>
              {statusIcons.REJECTED}
              {rejectedLabel}
            </DropdownItemSpan>
          ),
        },
      });
    }

    options.push({
      id: 'PINNED',
      name: {
        filterName: pinnedLabel,
        node: (
          <DropdownItemSpan>
            {statusIcons.PINNED}
            {pinnedLabel}
          </DropdownItemSpan>
        ),
      },
    });
    options.push({
      id: 'UNPINNED',
      name: {
        filterName: unpinnedLabel,
        node: (
          <DropdownItemSpan>
            {statusIcons.UNPINNED}
            {unpinnedLabel}
          </DropdownItemSpan>
        ),
      },
    });

    return options;
  }, [t, statusIcons, hasAiEstimates]);

  const gcpOptions = useMemo<
    { id: string; name: string | { filterName: string; node: React.ReactNode } }[]
  >(() => {
    const allGcpLabel = t('reviewGcpMarkerEstimatesDialog.allGcp', { ns: 'cloudProcessing' });
    return [
      {
        id: 'all',
        name: allGcpLabel,
      },
      ...uniqueGcpsWithCounts.map((gcp) => ({ id: gcp.id, name: gcp.name })),
    ];
  }, [uniqueGcpsWithCounts, t]);

  if (showPlaceMarkersDialog) {
    return null;
  }

  const showRejectButton = selectedStatusFilter === 'PINNED' || selectedStatusFilter === 'ACCEPTED';

  return (
    <Dialog
      closeIcon={true}
      context={(children) => (
        <GeodataContext.Provider value={context}>{children}</GeodataContext.Provider>
      )}
      maxHeight="expand"
      width="70%"
      onClose={props.dialog.hide}
    >
      {{
        header: t('reviewGcpMarkerEstimatesDialog.header', { ns: 'cloudProcessing' }),
        content: (
          <ContentContainer>
            <ControlsContainer>
              <LabelledContainer
                text={t('reviewGcpMarkerEstimatesDialog.filterByGcp', { ns: 'cloudProcessing' })}
              >
                {(formElementId) => (
                  <DropdownSuggestion<{
                    id: string;
                    name: string | { filterName: string; node: React.ReactNode };
                  }>
                    id={formElementId()}
                    portalToBody={true}
                    suggestions={gcpOptions}
                    value={selectedGcpFilter || 'all'}
                    width="100%"
                    onSuggestionSelected={(suggestion) => {
                      setSelectedGcpFilter(suggestion.id);
                      setCurrentPage(1); // Reset to first page when filter changes
                    }}
                  />
                )}
              </LabelledContainer>
              <LabelledContainer
                text={t('reviewGcpMarkerEstimatesDialog.filterByStatus', { ns: 'cloudProcessing' })}
              >
                {(formElementId) => (
                  <DropdownSuggestion<{
                    id: MarkerStatus;
                    name: string | { filterName: string; node: React.ReactNode };
                  }>
                    id={formElementId()}
                    portalToBody={true}
                    suggestions={statusOptions}
                    value={selectedStatusFilter}
                    width="100%"
                    onSuggestionSelected={(suggestion) => {
                      setSelectedStatusFilter(suggestion.id);
                      setCurrentPage(1); // Reset to first page when filter changes
                    }}
                  />
                )}
              </LabelledContainer>
              <LabelledContainer
                text={t('reviewGcpMarkerEstimatesDialog.zoomLevel', { ns: 'cloudProcessing' })}
              >
                <Slider max={10} min={1} step={0.1} value={zoomLevel} onChange={setZoomLevel} />
              </LabelledContainer>
              {hasAiEstimates && (
                <LabelledContainer
                  text={`${t('reviewGcpMarkerEstimatesDialog.minConfidence', {
                    ns: 'cloudProcessing',
                  })} (${(minConfidence * 100).toFixed(0)}%)`}
                >
                  <Slider
                    max={1}
                    min={0}
                    step={0.01}
                    value={minConfidence}
                    onChange={(v) => {
                      setMinConfidence(v);
                      setCurrentPage(1);
                    }}
                  />
                </LabelledContainer>
              )}
              <CheckBox
                checked={autoSelection}
                disabled={disableEstimatesPerGcpLimit}
                onChange={(e) => setAutoSelection(e.currentTarget.checked)}
              >
                <AutoSelectionLabel>
                  {t('reviewGcpMarkerEstimatesDialog.automaticSelection', {
                    ns: 'cloudProcessing',
                  })}
                  <TooltipInfoIcon
                    content={t('reviewGcpMarkerEstimatesDialog.automaticSelectionTooltip', {
                      ns: 'cloudProcessing',
                    })}
                    title={t('reviewGcpMarkerEstimatesDialog.automaticSelection', {
                      ns: 'cloudProcessing',
                    })}
                  />
                </AutoSelectionLabel>
              </CheckBox>
              <LabelledContainer
                text={t('reviewGcpMarkerEstimatesDialog.limitPerGcp', { ns: 'cloudProcessing' })}
              >
                {(formElementId) => (
                  <TextBox
                    disabled={autoSelection || disableEstimatesPerGcpLimit}
                    id={formElementId()}
                    min={1}
                    placeholder={t('reviewGcpMarkerEstimatesDialog.showAll', {
                      ns: 'cloudProcessing',
                    })}
                    type="number"
                    value={estimatesPerGcpLimit}
                    onChange={(e) => setEstimatesPerGcpLimit(e.currentTarget.value)}
                  />
                )}
              </LabelledContainer>
              <LabelledContainer
                text={t('reviewGcpMarkerEstimatesDialog.itemsPerPage', { ns: 'cloudProcessing' })}
              >
                {(formElementId) => (
                  <TextBox
                    id={formElementId()}
                    min={1}
                    placeholder="20"
                    type="number"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(e.currentTarget.value);
                      setCurrentPage(1); // Reset to first page when items per page changes
                    }}
                  />
                )}
              </LabelledContainer>
            </ControlsContainer>
            {isLoadingEstimates ? (
              <EmptyStateContainer>
                <Icon
                  data-testid="loading-estimates"
                  icon={['fas', 'spinner']}
                  size="3x"
                  spin={true}
                />
              </EmptyStateContainer>
            ) : filteredMarkerItems.length === 0 ? (
              <EmptyStateContainer>
                <IconPanel
                  header={{
                    icon: {
                      icon: showDoneMessage ? ['fad', 'circle-check'] : ['fad', 'magnifying-glass'],
                      opacity: '50%',
                      color: showDoneMessage ? theme.color.green : undefined,
                      size: '8x',
                    },
                  }}
                  responsiveness={{ compressed: false }}
                >
                  {{
                    title: showDoneMessage
                      ? t('reviewGcpMarkerEstimatesDialog.emptyState.done', {
                          ns: 'cloudProcessing',
                        })
                      : t('reviewGcpMarkerEstimatesDialog.emptyState.noResult', {
                          ns: 'cloudProcessing',
                        }),
                    info: showDoneMessage
                      ? t('reviewGcpMarkerEstimatesDialog.emptyState.doneInfo', {
                          ns: 'cloudProcessing',
                          limit: isDefined(selectedGcpEffectiveLimit)
                            ? String(selectedGcpEffectiveLimit)
                            : estimatesPerGcpLimit,
                        })
                      : t('reviewGcpMarkerEstimatesDialog.emptyState.noResultInfo', {
                          ns: 'cloudProcessing',
                        }),
                  }}
                </IconPanel>
              </EmptyStateContainer>
            ) : (
              <CardsContent>
                <CardsContainer>
                  {currentPageItems.map((item) => (
                    <MarkerEstimateCard
                      disabled={!readyToOptimize}
                      estimate={item.estimate}
                      gcp={item.gcp}
                      geodataId={geodata.id}
                      image={item.image}
                      key={item.estimate?.id || `${item.markerId}-${item.status}`}
                      marker={item.marker}
                      markerId={item.markerId}
                      status={item.status}
                      zoomLevel={zoomLevel}
                      onAccept={handleAcceptEstimate}
                      onImageClick={() => handleImageClick(item)}
                      onPin={handlePinMarker}
                      onReject={handleRejectEstimate}
                      onUnpin={handleUnpinMarker}
                    />
                  ))}
                </CardsContainer>
              </CardsContent>
            )}
          </ContentContainer>
        ),
        footer: {
          left: (
            <>
              {!showRejectButton && (
                <Button
                  color="primary"
                  disabled={isPending || !canAcceptAny || !readyToOptimize}
                  variant="contained"
                  onClick={handleAcceptAllEstimates}
                >
                  {t('reviewGcpMarkerEstimatesDialog.acceptAllEstimatesWithCount', {
                    ns: 'cloudProcessing',
                    count: acceptAllCount,
                  })}
                </Button>
              )}
              {showRejectButton && (
                <Button
                  color="error"
                  disabled={isPending || isUnpinMarkersPending || !canRejectAny || !readyToOptimize}
                  leftIcon={{ icon: ['fad', 'xmark'] }}
                  loading={selectedStatusFilter === 'PINNED' ? isUnpinMarkersPending : isPending}
                  variant="contained"
                  onClick={handleRejectAll}
                >
                  {t('reviewGcpMarkerEstimatesDialog.rejectAllEstimatesWithCount', {
                    ns: 'cloudProcessing',
                    count: rejectAllCount,
                  })}
                </Button>
              )}
              <Button
                color="secondary"
                disabled={!hasPreviousPage}
                variant="contained"
                onClick={handlePreviousPage}
              >
                {t('previous', { ns: 'common' })}
              </Button>
              <Button
                color="secondary"
                disabled={!hasNextPage}
                variant="contained"
                onClick={handleNextPage}
              >
                {t('next', { ns: 'common' })}
              </Button>
            </>
          ),
          right: (
            <>
              <Button variant="text" onClick={props.dialog.hide}>
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const CardsContent = styled.div`
  display: flex;
  justify-content: center;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, 140px);
  gap: 1em;
  justify-content: start;
  max-width: 100%;
`;

const ContentContainer = styled.div`
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1em;
`;

const ControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
  padding: 0.5em 0;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  padding: 2em 1em;
  justify-content: center;
`;

const DropdownItemSpan = styled.span`
  display: flex;
  gap: 0.5em;
`;

const AutoSelectionLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 0.4em;
`;
